import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import {BaseEdge, EdgeProps, getSmoothStepPath} from 'reactflow';

import {assert} from 'Asserts';
import {ProcedureAccessLevel} from 'procedures/design/validation/determineProcedureAccessLevel';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import {GRAY_DARK} from 'ui-library/Colors';

import {ProcedureMethodAddNodeButton} from './buttons/ProcedureMethodAddNodeButton';
import {AppendDirection} from './flowchartManagementUtils';
import ProcedureEdgeLabelRenderer from './ProcedureEdgeLabelRenderer';
import {ProcedureEditorContext} from './ProcedureEditorContext';
import {MarkerEndId} from './ProcedureMarkerDefinition';
import {makeOpacityStyle, makeRawOpacityStyle} from './procedureMethodNodeCommonConsts';
import {MethodCanvasEdgeData} from './ProcedureReactFlowCanvasTypes';

export default function ProcedureMethodEdgeRenderer({
  id,
  data,
  sourceX,
  sourceY,
  targetX,
  targetY,
  selected,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
}: EdgeProps<MethodCanvasEdgeData>): JSX.Element {
  assert(data != null, 'node.data should always be present');

  const {accessLevel} = ProcedurePageContext.useContext();
  const {methodCidBeingDragged, isProcedureReadOnly} = ProcedureEditorContext.useContext();

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Parse SVG path for quadratic corners
  const qs = edgePath.split('Q ');
  // If no quadratic corner (straight line most likely), use the given center of the edge
  let nextX = labelX;
  let nextY = labelY;
  if (qs.length > 1) {
    /**
     * For branching support:
     *
     * SVG paths are always source to target
     * therefore, find the last Q quadratic command
     * and parse the first coordinate to get absolute x and y
     * for the plus button
     */
    const lastQ = qs.at(qs.length - 1);
    const firstPair = lastQ?.split(' ')[0];
    const coordinate = firstPair?.split(',');
    nextX = Number(coordinate?.[0] ?? 0);
    nextY = Number(coordinate?.[1] ?? 0);
  }

  const BUTTON_LOCATION_ADJUSTMENT = -9;
  const top = nextY + BUTTON_LOCATION_ADJUSTMENT;
  const left = nextX + BUTTON_LOCATION_ADJUSTMENT;
  const hasValidDropZone =
    data.fromMethodCid !== methodCidBeingDragged && data.toMethodCid !== methodCidBeingDragged;
  const edgePathStyle = {
    stroke: GRAY_DARK,
    strokeWidth: '1.5px',
    ...makeRawOpacityStyle(methodCidBeingDragged),
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={`url(#${MarkerEndId.VALID_EDGE_MARKER})`} style={edgePathStyle} />
      <ProcedureEdgeLabelRenderer>
        {/* These classes ensure ReactFlow doesn't allow certain behaviors with this edge */}
        <div
          className={classNames(
            'nodrag nopan',
            css(styles.edgeLabel, !hasValidDropZone && makeOpacityStyle(methodCidBeingDragged))
          )}
        >
          {!isProcedureReadOnly && accessLevel === ProcedureAccessLevel.FULL_ACCESS && (
            <ProcedureMethodAddNodeButton
              top={top}
              left={left}
              hasValidDropZone={hasValidDropZone}
              onClick={() => {
                data.onAddMethodClick(data.fromMethodCid, AppendDirection.DOWNSTREAM);
              }}
            />
          )}
        </div>
      </ProcedureEdgeLabelRenderer>
    </>
  );
}

const styles = StyleSheet.create({
  edgeLabel: {
    position: 'absolute',
    fontSize: 12,
    pointerEvents: 'all',
  },
});
